import React, { useState } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, CardHeader, CardTitle, Btn, FormGroup, FormGrid, Input, Textarea, SectionHead, InfoBanner } from '../../common/UI';

export default function ProfilePage() {
    const { role, showToast, loggedUser, updateUser } = useApp();

    const [localProfile, setLocalProfile] = useState({
        name: loggedUser?.name || '',
        email: loggedUser?.email || '',
        phone: loggedUser?.phone || '',
        role: role.charAt(0).toUpperCase() + role.slice(1),
        bio: loggedUser?.bio || '',
    });

    const [passwords, setPasswords] = useState({
        current: '',
        newPass: '',
        confirm: ''
    });

    const handleProfileUpdate = (e) => {
        e.preventDefault();
        if (!localProfile.name || !localProfile.email) {
            showToast('Name and Email are required.', 'error');
            return;
        }
        updateUser({
            name: localProfile.name,
            email: localProfile.email,
            phone: localProfile.phone,
            bio: localProfile.bio,
        });
        showToast('Profile updated successfully!', 'success');
    };

    const handlePasswordUpdate = (e) => {
        e.preventDefault();
        if (!passwords.current || !passwords.newPass || !passwords.confirm) {
            showToast('Please fill all password fields.', 'error');
            return;
        }
        if (passwords.newPass !== passwords.confirm) {
            showToast('New passwords do not match.', 'error');
            return;
        }
        showToast('Password changed successfully!', 'success');
        setPasswords({ current: '', newPass: '', confirm: '' });
    };

    return (
        <div className="fade-up" style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
            <SectionHead title="My Profile" subtitle="Manage your account settings and preferences" />

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr) 2fr', gap: 24, alignItems: 'start' }}>

                {/* Left Column: Avatar & Quick Info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    <Card style={{ textAlign: 'center', padding: '32px 20px' }}>
                        <div style={{
                            width: 120, height: 120, borderRadius: '50%', background: 'var(--accent-light)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48,
                            margin: '0 auto 16px', color: 'var(--accent)', border: '4px solid var(--surface2)',
                            position: 'relative', overflow: 'hidden'
                        }}>
                            {loggedUser?.photo ? (
                                <img src={loggedUser.photo} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                localProfile.name.split(' ').map(n => n[0]).join('').substring(0, 2)
                            )}

                            <label style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0, height: 34,
                                background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 13,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                opacity: 0, transition: 'opacity .2s'
                            }}
                                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                onMouseLeave={e => e.currentTarget.style.opacity = 0}
                            >
                                Upload
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => updateUser({ photo: reader.result });
                                        reader.readAsDataURL(file);
                                    }
                                }} />
                            </label>
                        </div>
                        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>{loggedUser?.name}</h2>
                        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>{loggedUser?.email}</p>

                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'var(--surface2)', borderRadius: 20, fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)' }}></span>
                            Active Account
                        </div>
                    </Card>
                </div>

                {/* Right Column: Forms */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {/* Personal Info Form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Personal Information</CardTitle>
                        </CardHeader>
                        <form onSubmit={handleProfileUpdate} style={{ padding: 20 }}>
                            <FormGrid>
                                <FormGroup label="Full Name" required>
                                    <Input value={localProfile.name} onChange={e => setLocalProfile({ ...localProfile, name: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Email Address" required>
                                    <Input type="email" value={localProfile.email} onChange={e => setLocalProfile({ ...localProfile, email: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Phone Number">
                                    <Input value={localProfile.phone} onChange={e => setLocalProfile({ ...localProfile, phone: e.target.value })} placeholder="+1 234 567 890" />
                                </FormGroup>
                                <FormGroup label="Role">
                                    <Input value={localProfile.role} disabled style={{ background: 'var(--surface2)', color: 'var(--muted)', cursor: 'not-allowed' }} />
                                </FormGroup>
                            </FormGrid>

                            <FormGroup label="About / Bio" style={{ marginTop: 14 }}>
                                <Textarea value={localProfile.bio} onChange={e => setLocalProfile({ ...localProfile, bio: e.target.value })} placeholder="Write something about yourself..." />
                            </FormGroup>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                                <Btn variant="primary" type="submit">Save Changes</Btn>
                            </div>
                        </form>
                    </Card>

                    {/* Security / Password Form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Security</CardTitle>
                        </CardHeader>
                        <form onSubmit={handlePasswordUpdate} style={{ padding: 20 }}>
                            <InfoBanner variant="blue">
                                ℹ Use a strong password with at least 8 characters, numbers, and symbols.
                            </InfoBanner>
                            <FormGroup label="Current Password" style={{ marginBottom: 14 }}>
                                <Input type="password" placeholder="Enter current password" value={passwords.current} onChange={e => setPasswords({ ...passwords, current: e.target.value })} />
                            </FormGroup>
                            <FormGrid>
                                <FormGroup label="New Password">
                                    <Input type="password" placeholder="New password" value={passwords.newPass} onChange={e => setPasswords({ ...passwords, newPass: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Confirm Password">
                                    <Input type="password" placeholder="Confirm new password" value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} />
                                </FormGroup>
                            </FormGrid>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                                <Btn variant="primary" type="submit">Update Password</Btn>
                            </div>
                        </form>
                    </Card>
                </div>

            </div>
        </div>
    );
}
